import React, { useState, useEffect, useRef, useMemo } from 'react'
import MatchedCardItem from './MatchedCardItem'
import { Button, Tabs } from '@hose/eui'
import { OutlinedTipsAdd, OutlinedDirectionDown } from '@hose/eui-icons'
import { Attachment, IFeeTypeMatchedItem, ISpecificationMatched, FeeType } from '../../types/dataMatched'
import classNames from 'classnames'
import './index.less'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core'
import { createPortal } from 'react-dom';
import { v1 as uuid } from 'uuid';
import AttachmentListItem from './AttachmentListItem'
import { getParamFromUrl } from '../../utils/tools'


export interface MatchedCardProps {
  cardTitle: string
  matchedData: ISpecificationMatched[] | IFeeTypeMatchedItem[]
  className?: string
  addButtonText?: string
  addAssignedText?: string
  unassignedFiles?: Attachment[]
  onAdd?: (tabItem?: IFeeTypeMatchedItem) => void
  enableDrag?: boolean
  deleteDisabled?: boolean
  disableTooltip?: string
  isCountdown?: boolean
  countdown?: number
  onCancelCountdown?: () => void
  onAttachmentMove?: (attachmentId: string, fromItemId: string, toItemId: string, insertIndex?: number) => void
  onDelete?: (dataSource: ISpecificationMatched | FeeType, tabItem?: IFeeTypeMatchedItem) => void
  onChange?: (dataSource: ISpecificationMatched | FeeType, tabItem?: IFeeTypeMatchedItem) => void
  onConfirm?: () => void
  onReset?: () => void
}

export const MatchedCard: React.FC<MatchedCardProps> = (props) => {
  const {
    cardTitle,
    addButtonText,
    addAssignedText,
    className,
    matchedData = [],
    unassignedFiles,
    enableDrag = false,
    disableTooltip,
    deleteDisabled,
    countdown,
    isCountdown,
    onCancelCountdown,
    onAttachmentMove,
    onDelete,
    onChange,
    onConfirm,
    onReset,
    onAdd,
  } = props

  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeTabKey, setActiveTabKey] = useState<string>('')
  const contentRef = useRef()

  const isMobile = useMemo(() => {
    return getParamFromUrl('platform') === 'mobile'
  }, [])

  // 设置默认的 activeTabKey
  useEffect(() => {
    if (matchedData.length > 0 && !activeTabKey) {
      const firstFeeTypeItem = matchedData.find(isFeeTypeAssignedItem)
      if (firstFeeTypeItem) {
        setActiveTabKey(firstFeeTypeItem.uuid)
      }
    }
  }, [matchedData, activeTabKey])

  // 传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px 移动距离后才开始拖拽
      },
    }),
    useSensor(KeyboardSensor)
  )

  // 拖拽处理函数
  const handleDragStart = (event: any) => {
    onCancelCountdown?.()
    setActiveId(event.active.id)
    console.log('拖拽开始:', {
      activeId: event.active.id,
      activeData: event.active.data.current
    })
  }

  const handleDragCancel = () => {
    setActiveId(null)
    console.log('拖拽取消')
  }

  const handleDragEnd = (event: any) => {
    const { active, over } = event
    setActiveId(null)
    if (!over) return
    const activeData = active.data.current
    const overData = over.data.current
    if (activeData?.type !== 'attachment') return

    const attachmentId = activeData.attachment.fileKey
    const fromItemId = activeData.parentId

    if (overData?.type === 'attachment') {
      const overAttachmentId = overData.attachment.fileKey
      const toItemId = overData.parentId

      if (fromItemId === toItemId) {
        // 同容器内排序：费用类型或规格模板
        const feeTypeTab = (matchedData as any[]).find(
          (it: any) => it.feeTypes && it.feeTypes.some((ft: any) => (ft.uuid || ft.id) === fromItemId)
        )
        const feeType = feeTypeTab?.feeTypes?.find((ft: any) => (ft.uuid || ft.id) === fromItemId)
        if (feeType) {
          const attachments = feeType.attachments
          const oldIndex = attachments.findIndex((att: any) => att.fileKey === attachmentId)
          const newIndex = attachments.findIndex((att: any) => att.fileKey === overAttachmentId)
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const insertIndex = newIndex
            onAttachmentMove?.(attachmentId, feeType.uuid || feeType.id, feeType.uuid || feeType.id, insertIndex)
          }
          return
        }

        const sourceItem = (matchedData as any[]).find((item: any) => (item.uuid || item.id) === fromItemId)
        if (sourceItem?.attachments) {
          const attachments = sourceItem.attachments
          const oldIndex = attachments.findIndex((att: any) => att.fileKey === attachmentId)
          const newIndex = attachments.findIndex((att: any) => att.fileKey === overAttachmentId)
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const insertIndex = newIndex
            onAttachmentMove?.(attachmentId, fromItemId, fromItemId, insertIndex)
          }
        }

        // 同容器内：未匹配文件区排序
        if (fromItemId.startsWith('unassigned-')) {
          const tabUuid = fromItemId.replace('unassigned-', '')
          const tab = (matchedData as any[]).find((it: any) => it.uuid === tabUuid)
          const list = tab?.unassignedFiles || []
          const oldIndex = list.findIndex((att: any) => att.fileKey === attachmentId)
          const newIndex = list.findIndex((att: any) => att.fileKey === overAttachmentId)
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            const insertIndex = newIndex
            onAttachmentMove?.(attachmentId, fromItemId, fromItemId, insertIndex)
          }
          return
        }
      } else {
        // 跨容器：attachment → attachment
        const fromTab = (matchedData as any[]).find((it: any) =>
          (it.feeTypes && it.feeTypes.some((ft: any) => (ft.uuid || ft.id) === fromItemId)) ||
          (fromItemId.startsWith('unassigned-') && it.uuid === fromItemId.replace('unassigned-', ''))
        )
        const toTab = (matchedData as any[]).find((it: any) =>
          (it.feeTypes && it.feeTypes.some((ft: any) => (ft.uuid || ft.id) === toItemId)) ||
          (toItemId.startsWith('unassigned-') && it.uuid === toItemId.replace('unassigned-', ''))
        )
        const fromFeeType = fromTab?.feeTypes?.find((ft: any) => (ft.uuid || ft.id) === fromItemId)
        const toFeeType = toTab?.feeTypes?.find((ft: any) => (ft.uuid || ft.id) === toItemId)
        const toIsUnassigned = toItemId.startsWith('unassigned-')

        if (toFeeType) {
          const targetAttachments = toFeeType.attachments
          const insertIndex = targetAttachments.findIndex((att: any) => att.fileKey === overAttachmentId)
          onAttachmentMove?.(
            attachmentId,
            (fromFeeType?.uuid || fromFeeType?.id || fromItemId),
            toFeeType.uuid || toFeeType.id,
            insertIndex !== -1 ? insertIndex : 0
          )
          return
        }

        // 目标为未匹配文件区
        if (toIsUnassigned && toTab) {
          const list = toTab.unassignedFiles || []
          const insertIndex = list.findIndex((att: any) => att.fileKey === overAttachmentId)
          onAttachmentMove?.(
            attachmentId,
            (fromFeeType?.uuid || fromFeeType?.id || fromItemId),
            toItemId,
            insertIndex !== -1 ? insertIndex : 0
          )
          return
        }

        const sourceItem = (matchedData as any[]).find((item: any) => (item.uuid || item.id) === fromItemId)
        const targetItem = (matchedData as any[]).find((item: any) => (item.uuid || item.id) === toItemId)
        if (sourceItem?.attachments && targetItem?.attachments) {
          const insertIndex = targetItem.attachments.findIndex((att: any) => att.fileKey === overAttachmentId)
          onAttachmentMove?.(attachmentId, fromItemId, toItemId, insertIndex !== -1 ? insertIndex : 0)
        }
      }
    } else if (overData?.type === 'container') {
      const toItemId = overData.itemId

      const fromTab = (matchedData as any[]).find((it: any) => it.feeTypes && it.feeTypes.some((ft: any) => (ft.uuid || ft.id) === fromItemId))
      const toTab = (matchedData as any[]).find((it: any) => it.feeTypes && it.feeTypes.some((ft: any) => (ft.uuid || ft.id) === toItemId))
      const fromFeeType = fromTab?.feeTypes?.find((ft: any) => (ft.uuid || ft.id) === fromItemId)
      const toFeeType = toTab?.feeTypes?.find((ft: any) => (ft.uuid || ft.id) === toItemId)
      const fromIsUnassigned = fromItemId.startsWith('unassigned-')

      if (fromFeeType && toFeeType && (fromFeeType.uuid || fromFeeType.id) !== (toFeeType.uuid || toFeeType.id)) {
        onAttachmentMove?.(attachmentId, fromFeeType.uuid || fromFeeType.id, toFeeType.uuid || toFeeType.id, 0)
        return
      }

      // from 未匹配区 → to 费类型容器
      if (fromIsUnassigned && toFeeType) {
        onAttachmentMove?.(attachmentId, fromItemId, toFeeType.uuid || toFeeType.id, 0)
        return
      }

      const sourceItem = (matchedData as any[]).find((item: any) => (item.uuid || item.id) === fromItemId)
      const targetItem = (matchedData as any[]).find((item: any) => (item.uuid || item.id) === toItemId)
      if (sourceItem?.attachments && targetItem?.attachments && fromItemId !== toItemId) {
        onAttachmentMove?.(attachmentId, fromItemId, toItemId, 0)
        return
      }

      if (toItemId.startsWith('unassigned-')) {
        onAttachmentMove?.(attachmentId, fromItemId, toItemId, 0)
      }
    }
  }

  // 类型守卫函数
  const isFeeTypeAssignedItem = (item: ISpecificationMatched | IFeeTypeMatchedItem): item is IFeeTypeMatchedItem => {
    return 'feeTypes' in item
  }

  const renderUnassignedFiles = (unassignedFiles: Attachment[], templateUuid: string) => {
    return <MatchedCardItem
      key={`${templateUuid}-unassigned-files`}
      dataSource={{
        id: `unassigned-${templateUuid}`,
        uuid: `unassigned-${templateUuid}`,
        attachments: unassignedFiles,
        name: <span>
          <span className='matched-card-item-header-title-bold'>未匹配文件</span>
          （可拖拽至现有费用类型分组或新建分组）
        </span>,
      }}
      isDraggable={enableDrag}
    />
  }

  const renderContent = () => {
    const isFeeType = matchedData.some(isFeeTypeAssignedItem)
    if (isFeeType) {
      const items = (matchedData as IFeeTypeMatchedItem[])
        .filter(isFeeTypeAssignedItem)
        .map((item) => ({
          label: item.templateName,
          key: item.uuid,
          children: <>
            {Array.isArray(item.feeTypes) ? item.feeTypes.map(feeType => {
              const itemData = { ...feeType, uuid: feeType.uuid || `uuid_${uuid()}` }
              return (
                <MatchedCardItem
                  key={itemData.uuid}
                  dataSource={itemData}
                  onDelete={() => { onCancelCountdown?.(); onDelete?.(itemData, item) }}
                  onChange={() => { onCancelCountdown?.(); onChange?.(itemData, item) }}
                  tabItem={item}
                  isDraggable={enableDrag}
                />
              )
            }) : null}
            {addAssignedText && <Button
              category='text'
              theme='highlight'
              size={isMobile ? 'small' : 'middle'}
              icon={<OutlinedTipsAdd />}
              style={{margin:'8px 0'}}
              onClick={() => { onCancelCountdown?.(); onAdd?.(item) }}
            >{addAssignedText}</Button>}
            {renderUnassignedFiles(item.unassignedFiles || [], item.uuid)} {/* 改为使用唯一 tab uuid */}
          </>
        }))
      return <Tabs 
      activeKey={activeTabKey}
      items={items} 
      className='matched-card-tabs' 
      moreIcon={<span className='more-switch-icon'>
        更多
        <OutlinedDirectionDown />
      </span>}
      onChange={(key) => {
        onCancelCountdown?.();
        setActiveTabKey(key)
      }} />
    }
    return matchedData
      .filter((item): item is ISpecificationMatched => !isFeeTypeAssignedItem(item))
      .map(item => (
        <MatchedCardItem
          onDelete={(ds, tab) => { onCancelCountdown?.(); onDelete?.(ds, tab) }}
          onChange={(ds, tab) => { onCancelCountdown?.(); onChange?.(ds, tab) }}
          key={item.uuid}
          dataSource={item as any}
          showLabel={true}
          isDraggable={enableDrag}
          deleteDisabled={deleteDisabled}
          disableTooltip={disableTooltip}
        />
      ))
  }


  const content = (
    <div className={classNames('matched-card', className)}>
      <header className='matched-card-header'>{cardTitle}</header>
      {renderContent()}
      <footer className={classNames('matched-card-footer', { 'is-mobile': isMobile })}>
        {addButtonText ? <Button
          category='text'
          theme='highlight'
          size={isMobile ? 'small' : 'middle'}
          icon={<OutlinedTipsAdd />}
          onClick={() => { onCancelCountdown?.(); onAdd?.() }}
        >{addButtonText}</Button> : <div />}
        <div className='matched-card-actions'>
          <Button category='secondary' size={isMobile ? 'small' : 'middle'} onClick={() => { onCancelCountdown?.(); onReset?.() }}>重置</Button>
          <Button category='primary' size={isMobile ? 'small' : 'middle'} theme='highlight' onClick={onConfirm}>
            {isCountdown ? `确认(${countdown}s)` : '确认'}
          </Button>
        </div>
      </footer>
    </div>
  )

  if (!enableDrag) {
    return content
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={false}
    >
      {content}
      {
        contentRef.current && createPortal(
          <DragOverlay
            adjustScale
            style={{
              pointerEvents: 'none',
              zIndex: 9999
            }}
          >
            {activeId ? (
              <AttachmentListItem
                dataSource={(() => {
                  // 在所有数据中查找附件
                  const allAttachments: any[] = []

                  // 从 matchedData 中收集所有附件
                  for (const item of matchedData) {
                    if (isFeeTypeAssignedItem(item)) {
                      // FeeType 场景
                      if (item.feeTypes && Array.isArray(item.feeTypes)) {
                        for (const feeType of item.feeTypes) {
                          if (feeType.attachments && Array.isArray(feeType.attachments)) {
                            allAttachments.push(...feeType.attachments)
                          }
                        }
                      }
                      // 添加未匹配文件
                      if (item.unassignedFiles && Array.isArray(item.unassignedFiles)) {
                        allAttachments.push(...item.unassignedFiles)
                      }
                    } else {
                      // ISpecificationMatched 场景
                      if (item.attachments && Array.isArray(item.attachments)) {
                        allAttachments.push(...item.attachments)
                      }
                    }
                  }

                  // 添加传入的 unassignedFiles
                  if (unassignedFiles && Array.isArray(unassignedFiles)) {
                    allAttachments.push(...unassignedFiles)
                  }

                  const foundAttachment = allAttachments.find(attachment =>
                    attachment && attachment.fileKey === activeId
                  )

                  console.log('DragOverlay 查找附件:', {
                    activeId,
                    allAttachments: allAttachments.length,
                    foundAttachment: foundAttachment ? foundAttachment.fileName : 'Not found'
                  })

                  return foundAttachment || { fileKey: activeId || '', fileName: 'Unknown', fileUrl: '' }
                })()}
                isDraggable={true}
                isDragging={true}
              />
            ) : null}
          </DragOverlay>,
          contentRef.current
        )
      }

    </DndContext>
  )
}

export default MatchedCard